import { Stack, Flex } from "@/components/layout";
import { Text } from "@/components/typography";
import { Badge, Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui";
import { ACTION_LABEL, type CatalogueModule } from "./api";

/**
 * The module checkboxes. A box is greyed out when the level cannot hold it
 * (Admin-only modules for an Employee) or when the person making the change
 * does not hold it themselves (an Admin cannot give what they lack). The
 * server and the database refuse the same things; this only says so early.
 */
export function StaffAccessGrid({
  modules,
  tier,
  selected,
  onChange,
  disabled,
}: {
  modules: CatalogueModule[];
  tier: "admin" | "employee";
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}) {
  const toggle = (module: string, action: string, on: boolean) => {
    const next = new Set(selected);
    if (on) {
      next.add(`${module}:${action}`);
      if (action !== "view") next.add(`${module}:view`);
    } else {
      next.delete(`${module}:${action}`);
      if (action === "view") {
        next.delete(`${module}:manage`);
        next.delete(`${module}:view_all`);
      }
    }
    onChange(next);
  };

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Module</Th>
          <Th>View</Th>
          <Th>Manage</Th>
          <Th>See everyone&apos;s work</Th>
        </Tr>
      </Thead>
      <Tbody>
        {modules.map((m) => {
          const levelBlocked = m.min_tier === "admin" && tier === "employee";
          return (
            <Tr key={m.module}>
              <Td>
                <Stack gap={4}>
                  <Flex gap={8} align="center">
                    <Text role="label" as="span">{m.label}</Text>
                    {m.sensitive ? <Badge>Sensitive</Badge> : null}
                    {m.min_tier === "admin" ? <Badge>Admin level</Badge> : null}
                  </Flex>
                  <Text role="caption" color="secondary" as="span">{m.description}</Text>
                </Stack>
              </Td>
              {(["view", "manage", "view_all"] as const).map((action) => {
                const def = m.actions.find((a) => a.action === action);
                if (!def) return <Td key={action}><Text role="caption" color="muted" as="span">Not used</Text></Td>;
                const checked = selected.has(`${m.module}:${action}`);
                const lockedReason = levelBlocked
                  ? "Only Admins can hold this."
                  : !def.tickable
                    ? "You don't have this yourself, so you can't change it."
                    : null;
                return (
                  <Td key={action}>
                    <label title={lockedReason ?? ACTION_LABEL[action]} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        aria-label={`${m.label}: ${ACTION_LABEL[action]}`}
                        checked={checked}
                        disabled={disabled || !!lockedReason}
                        onChange={(e) => toggle(m.module, action, e.target.checked)}
                      />
                    </label>
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}
